<template>
  <customer-page
    :is-pdf-export-enabled="isPDfExportEnabled"
    :is-complete-pdf-export="isCompletePdfExport"
    @delete-customer="deleteCustomer"
    @get-pdf-report="getPdfReport"
  />
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { downloadBase64 } from '@/utils/download';
import CustomerPage from './CustomerPage';
import { showToast } from '@inscribe/signal';

export default {
  components: {
    'customer-page': CustomerPage,
  },
  beforeRouteEnter(to, from, next) {
    const customersRegex = /^\/customers\//;
    if (customersRegex.test(to.path)) {
      const nextPath = to.path
        .replace(customersRegex, '/customer/')
        .replace('documents', 'document')
        .replace('info/text', 'text')
        .replace('forensics/metadata', 'metadata')
        .replace('forensics/seen_before', 'seen-before')
        .replace(/databases\/.+/, 'signals')
        .replace(/forensics\/.+/, 'signals');
      next(nextPath);
    } else {
      next();
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.customer_id !== from.params.customer_id) {
      this.$store.state.axiosCancelSource.cancel('User cancelled request');
      this.$store.commit('setCancellationToken');
      this.$store.commit('setDocuments', []);
    }
    next();
  },
  data() {
    return {
      customerId: this.$route.params.customer_id,
      isProcessingDocuments: false,
      isCompletePdfExport: true,
    };
  },
  computed: {
    ...mapState({
      selectedCustomer: (state) => state.customers.selectedCustomer,
    }),
    ...mapGetters({
      documentsListEmpty: 'documentsListEmpty',
    }),
    isCustomerApproved() {
      return this.selectedCustomer && this.selectedCustomer.approved === true;
    },
    isCustomerApprovalNotNull() {
      return this.selectedCustomer && this.selectedCustomer.approved !== null;
    },
    isPDfExportEnabled() {
      return !this.documentsListEmpty;
    },
  },
  watch: {
    selectedCustomer: {
      immediate: true,
      handler: function (newCustomer, oldCustomer) {
        if (
          !!this.selectedCustomer &&
          (!oldCustomer || newCustomer.id !== oldCustomer.id)
        ) {
          this.fetchDocuments();
          this.$store.commit('setDocumentsMetadata', undefined);
        }
      },
    },
    '$route.query.page': function () {
      this.fetchDocuments();
    },
  },
  mounted() {
    if (this.documentsListEmpty) {
      this.setDocumentsLoading(true);
    }
  },
  methods: {
    ...mapActions(['getPaginatedDocuments', 'getDocumentsForCustomer']),
    ...mapMutations(['setDocumentsLoading']),
    fetchDocuments() {
      const { selectedCustomer } = this.$store.state.customers;
      if (selectedCustomer) {
        this.setDocumentsLoading(true);
        this.getPaginatedDocuments({
          customerId: selectedCustomer.id,
          page: this.$route.query.page || 1,
          risk_levels: this.$route.query.risk_levels || [],
        })
          .then(({ data }) => {
            this.setDocumentsLoading(false);
            this.$store.commit('setDocuments', data.data);

            if (data.metadata) {
              const pagination = data.metadata.pagination;
              const fraudResults = data.metadata.fraud_results;
              this.$store.commit('setDocumentsMetadata', {
                pagination,
                fraudResults,
              });
            }
          })
          .catch(() => this.setDocumentsLoading(false));
      }
    },
    uploadCompleted(files) {
      this.getDocumentsForCustomer({
        customerId: this.$route.params.customer_id,
      })
        .then(({ data }) => {
          this.$store.commit('setDocuments', data.documents || data.data);
        })
        .catch(() => null);
    },
    getPdfReport() {
      this.$set(this, 'isCompletePdfExport', false);
      const customerName = this.selectedCustomer.name.toLowerCase();
      this.$store
        .dispatch('getCustomerExportPdfReport', {
          customer_id: this.$route.params.customer_id,
        })
        .then((response) => {
          const filename = customerName + '_results.pdf';
          downloadBase64(response.data.pdf, filename, 'application/pdf');
          this.$set(this, 'isCompletePdfExport', true);
        });
    },
    setIsProcessing(state) {
      this.$set(this, 'isProcessingDocuments', state);
    },
    deleteCustomer() {
      if (
        confirm(
          `Are you sure you want to delete the customer "${this.selectedCustomer.name}"?`,
        )
      ) {
        this.$store
          .dispatch('deleteCustomer', this.$route.params.customer_id)
          .then(
            (response) => {
              this.$router.push({
                name: 'customers-view',
                query: {
                  page: 1,
                  search: undefined,
                },
              });
              showToast({
                text: 'Customer deleted successfully.',
              });
            },
            (errorResponse) => {
              if (
                errorResponse.response !== undefined &&
                errorResponse.response.status === 403
              ) {
                showToast({
                  title: 'Permission Denied',
                  text: 'You do not have permission to delete this customer. Please contact your admin if you require this permission.',
                  isError: true,
                });
              } else {
                showToast({
                  text: 'Could not delete customer, please try again or contact Inscribe support.',
                  isError: true,
                });
              }
            },
          );
      }
    },
  },
};
</script>
